import { useTranslation } from 'react-i18next';
import { formatNumber, formatPercent } from '../../i18n';
import type { ReportScreenRow } from './buildReportRows';

type ReportsScreenTableProps = {
  rows: ReportScreenRow[];
};

const COLS =
  'minmax(0, 1.6fr) minmax(0, 1.1fr) minmax(0, 1.4fr) minmax(0, 0.7fr) minmax(0, 0.7fr)';

export function ReportsScreenTable({ rows }: ReportsScreenTableProps) {
  const { t } = useTranslation('reports');
  return (
    <section
      className="w-full overflow-x-auto rounded-[14px] border"
      style={{ background: 'var(--gs-panel)', borderColor: 'var(--gs-line)' }}
    >
      <div className="min-w-[720px]">
        <div
          className="grid gap-3.5 border-b px-5 py-[13px] text-[11.5px] font-bold uppercase tracking-[0.12em]"
          style={{
            gridTemplateColumns: COLS,
            borderColor: 'var(--gs-line)',
            color: 'var(--gs-muted)',
          }}
        >
          <span>{t('table.screen')}</span>
          <span>{t('table.playing')}</span>
          <span>{t('table.uptime')}</span>
          <span>{t('table.onAir')}</span>
          <span>{t('table.plays')}</span>
        </div>

        {rows.length === 0 ? (
          <div className="px-5 py-[22px] text-sm" style={{ color: 'var(--gs-muted)' }}>
            {t('table.empty')}
          </div>
        ) : (
          rows.map((row) => (
            <div
              key={row.screenId}
              className="grid items-center gap-3.5 border-b px-5 py-3.5 last:border-b-0"
              style={{
                gridTemplateColumns: COLS,
                borderColor: 'var(--gs-line)',
              }}
            >
              <span className="flex min-w-0 flex-col gap-[3px]">
                <span className="truncate text-[14.5px] font-bold">{row.name}</span>
                <span className="truncate text-xs" style={{ color: 'var(--gs-muted)' }}>
                  {row.location ?? t('table.unknownLocation')}
                </span>
              </span>
              <span className="truncate text-[13.5px]" style={{ color: 'var(--gs-ink2)' }}>
                {row.playlist === null
                  ? t('table.nothingAssigned')
                  : row.playlist || t('untitledPlaylist')}
              </span>
              <span className="flex items-center gap-2.5">
                <span
                  className="h-[7px] flex-1 overflow-hidden rounded"
                  style={{ background: 'var(--gs-panel2)' }}
                >
                  <span
                    className="block h-full"
                    style={{ width: `${row.uptimePct}%`, background: row.uptimeColor }}
                  />
                </span>
                <span className="min-w-[36px] text-[13px] font-bold">
                  {formatPercent(row.uptimePct / 100)}
                </span>
              </span>
              <span className="text-[13.5px]">
                {t('table.hoursOnAir', { value: row.hoursOnAir })}
              </span>
              <span className="text-[13.5px]">{formatNumber(row.plays)}</span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
